import { Box, Button, Text, useColorModeValue, useDisclosure, useToast } from "@chakra-ui/react";
import FormModal from "../../../components/FormModal";
import FormField from "../../../components/fields/FormField";
import { useState } from "react";
import PageCard from "../../../components/page-card";
import { translate } from "../../../i18n";
import { useSelectedOrganization } from "../../organization/hooks/useSelectedOrganization";
import { useUpdateOrganizationDiscount } from "../../organization/hooks/useUpdateOrganizationDiscount";

export default function DiscountPolicyCard() {
    const { selectedOrganization } = useSelectedOrganization();
    const textColorPrimary = useColorModeValue('secondaryGray.900', 'white');
    const toast = useToast();

    const { isOpen, onOpen, onClose } = useDisclosure();
    const [discountPercentage, setDiscountPercentage] = useState<string>(
        String(selectedOrganization?.lastMinuteDiscountPercentage || 0)
    );
    const [discountDurationHours, setDiscountDurationHours] = useState<string>(
        String(Math.floor((selectedOrganization?.lastMinuteDiscountDuration || 0) / 3600000)) // ms to hours
    );

    const { mutateAsync: updateOrganizationDiscount, isLoading: isUpdatingDiscount } =
        useUpdateOrganizationDiscount(selectedOrganization?.id || '', () => {
            onClose();
        });

    const handleUpdateDiscount = async () => {
        try {
            const percentageValue = parseFloat(discountPercentage) || 0;
            const durationValue = parseFloat(discountDurationHours) || 0;
            const discountDurationMilliseconds = durationValue * 3600000; // hours to ms
            await updateOrganizationDiscount({
                lastMinuteDiscountPercentage: percentageValue,
                lastMinuteDiscountDuration: discountDurationMilliseconds
            });
        } catch {
            toast({ title: translate('error'), status: 'error', duration: 3000, position: 'top-right' });
        }
    };

    if (!selectedOrganization) return null;

    return (
        <>
            <PageCard
                title={translate('organization.last_minute_discount')}
            >
                <Box p={4}>
                    <Box mb={4} color={textColorPrimary}>
                        <Box mb={2}>
                            <strong>{translate('organization.discount_percentage')}: </strong>
                            {selectedOrganization.lastMinuteDiscountPercentage}%
                        </Box>
                        <Box mb={2}>
                            <strong>{translate('organization.discount_duration')}: </strong>
                            {Math.floor((selectedOrganization.lastMinuteDiscountDuration || 0) / 3600000)} {translate('hours')}
                        </Box>
                    </Box>
                    <Button
                        mt={3}
                        size="md"
                        onClick={() => {
                            onOpen();
                            setDiscountPercentage(String(selectedOrganization?.lastMinuteDiscountPercentage || 0));
                            setDiscountDurationHours(String(Math.floor((selectedOrganization?.lastMinuteDiscountDuration || 0) / 3600000)));
                        }}
                        colorScheme="brand"
                    >
                        {translate('organization.set_discount_settings')}
                    </Button>
                </Box>
            </PageCard>

            <FormModal
                isOpen={isOpen}
                onClose={onClose}
                title={translate('organization.set_discount_settings')}
                onSubmit={handleUpdateDiscount}
                submitLabel={translate('save')}
                cancelLabel={translate('common.cancel')}
                isSubmitting={isUpdatingDiscount}
            >
                <Text fontSize={14} mb={4}>
                    {translate('organization.discount_settings_description')}
                </Text>

                <Box mb={4}>
                    <FormField
                        label={translate('organization.discount_percentage')}
                        value={discountPercentage}
                        onChange={setDiscountPercentage}
                        inputProps={{ type: 'number', min: '0', max: '100' }}
                    />
                </Box>

                <Box mb={4}>
                    <FormField
                        label={translate('organization.discount_duration')}
                        value={discountDurationHours}
                        onChange={setDiscountDurationHours}
                        inputProps={{ type: 'number', min: '0' }}
                    />
                </Box>
            </FormModal>
        </>
    );
}
